/**
 * DisputeArgumentsList - Immutable log of dispute interventions
 *
 * DevStory 4.5:
 * Criterio 7 - Argumentos de la parte contraria
 * Criterio 8 - Trazabilidad de argumentos (autor, timestamp, tipo de intervención, inmutabilidad)
 */

import { useState, useCallback } from 'react';
import { Button } from '@components/ui';
import { useAddDisputeArgument } from '@hooks/useDisputes';
import type { Dispute, DisputeArgument, DisputeArgumentType } from '@/types';
import { useAuthStore } from '@/stores/authStore';
import { pendingDisputeEntry } from '@/api/adapter/disputes';

interface DisputeArgumentsListProps {
  dispute: Dispute;
  argumentsList: DisputeArgument[];
  isClosed?: boolean;
  canWrite?: boolean;
}

export function DisputeArgumentsList({
  dispute,
  argumentsList,
  isClosed = false,
  canWrite = false,
}: DisputeArgumentsListProps): JSX.Element {
  const account = useAuthStore((s) => s.session?.accountId);
  const [draft] = useState(() => pendingDisputeEntry(dispute.id, 'arguments'));
  const [newContent, setNewContent] = useState(draft?.content ?? '');
  const [argumentType, setArgumentType] = useState<DisputeArgumentType>(
    draft?.argumentType === 'ADDITIONAL' ? 'ADDITIONAL' : 'RESPONSE'
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const addArgumentMutation = useAddDisputeArgument();

  const handleAddArgument = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = newContent.trim();
      if (!trimmed) {
        setErrorMsg('Argument content is required');
        return;
      }

      setErrorMsg(null);
      addArgumentMutation.mutate(
        {
          disputeId: dispute.id,
          content: trimmed,
          argumentType,
        },
        {
          onSuccess: () => {
            setNewContent('');
            setArgumentType('ADDITIONAL');
          },
          onError: (err) => {
            setErrorMsg(err.message || 'Failed to submit argument');
          },
        }
      );
    },
    [newContent, dispute.id, argumentType, addArgumentMutation]
  );

  const formatTypeLabel = (type: DisputeArgumentType) => {
    switch (type) {
      case 'OPENING':
        return {
          label: 'Initial Claim (Opening)',
          bg: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
        };
      case 'RESPONSE':
        return {
          label: 'Counter-Argument (Response)',
          bg: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
        };
      case 'ADDITIONAL':
        return {
          label: 'Additional Evidence',
          bg: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
        };
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b border-[var(--base-border,#3d3d3d)] pb-2">
        <div>
          <h3 className="text-base font-bold text-[var(--text-dark-primary,#f5f5f5)]">
            Case Arguments & Evidence ({argumentsList.length})
          </h3>
          <p className="text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
            Chronological, immutable audit record submitted by all participating
            parties
          </p>
        </div>
        <span className="flex items-center gap-1 text-xs text-[var(--state-brand-active,#36d399)]">
          <i className="ri-shield-check-line" /> Immutable Record
        </span>
      </div>

      {/* Arguments Timeline */}
      <div className="space-y-4">
        {argumentsList.length === 0 ? (
          <div className="py-8 text-center text-sm text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
            No arguments recorded yet.
          </div>
        ) : (
          argumentsList.map((arg, idx) => {
            const typeInfo = formatTypeLabel(arg.argumentType);
            const isAuthor = account === arg.userId;
            return (
              <div
                key={arg.id || idx}
                className="space-y-3 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-5 transition-all hover:border-[var(--base-border,#555)]"
              >
                {/* Author row */}
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] font-bold text-white">
                      {(arg.userName?.[0] || 'U').toUpperCase()}
                    </span>
                    <div>
                      <span className="font-semibold text-white">
                        {arg.userName || 'Participant'}
                      </span>
                      {arg.userRole && (
                        <span className="ml-1.5 rounded border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
                          {arg.userRole}
                        </span>
                      )}
                      {isAuthor && (
                        <span className="ml-1 text-[10px] text-[var(--state-brand-active,#36d399)]">
                          (You)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${typeInfo.bg}`}
                    >
                      {typeInfo.label}
                    </span>
                    <span className="text-[var(--text-dark-secondary,rgba(255,255,255,0.5))]">
                      {new Date(arg.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="whitespace-pre-wrap border-t border-[var(--base-border,#3d3d3d)] pt-2 text-sm leading-relaxed text-[var(--text-dark-primary,#f5f5f5)]">
                  {arg.content}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add New Argument Form (Criterio 7) */}
      {!isClosed && canWrite ? (
        <form
          onSubmit={handleAddArgument}
          className="space-y-4 rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-2,#231f1f)] p-5"
        >
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-bold text-white">
              <i className="ri-chat-new-line text-amber-400" />
              Submit Counter-Argument or Additional Evidence
            </h4>
            <div className="flex items-center gap-2 text-xs">
              <label
                htmlFor="dispute-argument-type"
                className="text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]"
              >
                Type:
              </label>
              <select
                id="dispute-argument-type"
                value={argumentType}
                onChange={(e) =>
                  setArgumentType(e.target.value as DisputeArgumentType)
                }
                className="rounded-lg border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] px-2.5 py-1 text-xs text-white focus:border-amber-400 focus:outline-none"
              >
                <option value="RESPONSE">Counter-Argument (Response)</option>
                <option value="ADDITIONAL">Additional Evidence</option>
              </select>
            </div>
          </div>

          <textarea
            value={newContent}
            onChange={(e) => {
              setNewContent(e.target.value);
              if (errorMsg) setErrorMsg(null);
            }}
            placeholder="Write your formal response, evidence, or supporting argument for the DAO..."
            rows={4}
            required
            aria-label="Argument content"
            className="w-full resize-none rounded-xl border border-[var(--base-border,#3d3d3d)] bg-[var(--base-surface-1,#141414)] px-4 py-3 text-sm text-[var(--text-dark-primary,#f5f5f5)] placeholder:text-[var(--text-dark-secondary,rgba(255,255,255,0.4))] focus:outline-none focus:ring-1 focus:ring-amber-400"
          />

          {errorMsg && (
            <p role="alert" className="text-xs text-rose-400">
              {errorMsg}
            </p>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              variant="primary"
              isLoading={addArgumentMutation.isPending}
              disabled={addArgumentMutation.isPending || !newContent.trim()}
              className="!border-amber-600 !bg-amber-600 text-xs font-semibold hover:!bg-amber-700"
            >
              <i className="ri-send-plane-line mr-1.5" />
              Append to Dispute Record
            </Button>
          </div>
        </form>
      ) : (
        <div className="rounded-xl border border-gray-500/20 bg-gray-500/5 p-4 text-center text-xs text-[var(--text-dark-secondary,rgba(255,255,255,0.7))]">
          {isClosed
            ? 'This dispute is closed. No further arguments can be added.'
            : 'Only the client and coordinator can append to this record.'}
        </div>
      )}
    </div>
  );
}
